import React, { useEffect, useRef, useState } from 'react';
import { AdvancedImage, lazyload, placeholder, responsive } from '@cloudinary/react';
import { useCloudinary } from '../../hooks/Cloudinary/useCloudinary';
import type { CloudinaryVideoPlayerOptions } from '../../hooks/Cloudinary/useCloudinaryWithVideoPlayer.types';
import { CloudinaryVideoType, CloudinaryImageType } from '../../../../types/CloudinaryImageType';

export interface VideoPlayerProps {
  video: CloudinaryVideoType;
  onVideoPlay?: () => any;
  onVideoStart?: () => any;
  isAccessibleForUser?: boolean;
  videoPlayerOptions?: CloudinaryVideoPlayerOptions;
  image?: CloudinaryImageType;
  /** Set to `false` to skip the poster/thumbnail entirely (e.g. ArticleHeader, which shows its own loader). */
  showPoster?: boolean;
}

export const DynamicVideoPlayer = ({
  video,
  onVideoStart,
  onVideoPlay,
  isAccessibleForUser = true,
  videoPlayerOptions,
  image,
  showPoster = true,
}: VideoPlayerProps) => {
  const [DynamicallyImportedVideoPlayer, setDynamicallyImportedVideoPlayer] = useState<any>(null);
  const { getVideo } = useCloudinary();

  const importVideoPlayer = async () => {
    const VideoPlayerModule = await import('./VideoPlayerModule');
    setDynamicallyImportedVideoPlayer(VideoPlayerModule);
  };

  const poster = showPoster
    ? getVideo(video?.publicId, {
        width: 768,
        resizeMethod: 'limitFill',
      })
    : undefined;

  const observerTarget = useRef(null);

  useEffect(() => {
    const ref = observerTarget.current;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          importVideoPlayer();
        }
      },
      { threshold: 0, rootMargin: '0px 0px 400px 0px' },
    );

    if (ref) {
      observer.observe(ref);
    }

    return () => {
      if (ref) {
        observer.unobserve(ref);
      }
    };
  }, [observerTarget]);

  if (DynamicallyImportedVideoPlayer && video && isAccessibleForUser) {
    return (
      <DynamicallyImportedVideoPlayer.VideoPlayer
        videoPlayerOptions={videoPlayerOptions}
        video={video}
        onVideoStart={onVideoStart}
        onVideoPlay={onVideoPlay}
        image={image}
        showPoster={showPoster}
      />
    );
  }

  if (!showPoster) {
    // A bare, src-less <video> so Simba's ArticleHeader detects it and shows its own
    // loading overlay while the player module lazy-loads; also the observer's target.
    // eslint-disable-next-line jsx-a11y/media-has-caption
    return <video ref={observerTarget} muted playsInline />;
  }

  return poster ? (
    <div ref={observerTarget}>
      <AdvancedImage
        style={{ maxWidth: '100%' }}
        cldImg={poster}
        plugins={[
          lazyload({ rootMargin: '10px 20px 10px 30px', threshold: 0.25 }),
          responsive({ steps: 200 }),
          placeholder({ mode: 'blur' }),
        ]}
      />
    </div>
  ) : (
    <div ref={observerTarget} />
  );
};
